"use client";

import { Check, Coins } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import {
  PAYG_MAX_GENERATIONS,
  PAYG_MIN_GENERATIONS,
  PAYG_PRICE_PER_GEN_MAD,
  paygTotalMadMarketing,
} from "@/lib/pricingConstants";
import {
  CURRENCY,
  formatMadAmount,
  PAYG_CARD_COPY,
} from "./pricingPageCopy";

export function PaygCreditsCard() {
  const [credits, setCredits] = useState<number>(PAYG_MIN_GENERATIONS);
  const totalMad = paygTotalMadMarketing(credits);

  return (
    <article className="flex h-full min-w-0 flex-col rounded-[24px] border border-slate-200 bg-gradient-to-b from-slate-50 via-white to-white p-6 text-center shadow-[0_28px_90px_-58px_rgba(15,23,42,0.5)] sm:p-7">
      <div className="flex items-center justify-center gap-3 text-sm font-black uppercase text-slate-700">
        <Coins className="h-6 w-6" strokeWidth={2.4} aria-hidden />
        <span>{PAYG_CARD_COPY.label}</span>
      </div>

      <div className="mt-6">
        <p className="flex items-baseline justify-center gap-2 text-[44px] font-black leading-none text-slate-950">
          <span>{formatMadAmount(PAYG_PRICE_PER_GEN_MAD)}</span>
          <span className="text-[22px] font-black">{CURRENCY}</span>
        </p>
        <p className="mt-3 text-base font-black leading-tight text-slate-600">
          {PAYG_CARD_COPY.priceCaption}
        </p>
      </div>

      <p className="mx-auto mt-5 max-w-[250px] text-pretty text-[16px] font-medium leading-7 text-slate-700">
        {PAYG_CARD_COPY.tagline}
      </p>

      <div className="my-7 border-t border-slate-200" aria-hidden />

      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm">
        <div className="flex items-end justify-between gap-4">
          <div>
            <label htmlFor="payg-credit-slider" className="text-sm font-black text-slate-950">
              {PAYG_CARD_COPY.sliderLabel}
            </label>
            <p className="mt-1 text-sm font-medium text-slate-500">
              {PAYG_MIN_GENERATIONS} to {PAYG_MAX_GENERATIONS} credits
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black leading-none text-slate-950">{credits}</p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
              {formatMadAmount(totalMad)} {CURRENCY}
            </p>
          </div>
        </div>

        <input
          id="payg-credit-slider"
          type="range"
          min={PAYG_MIN_GENERATIONS}
          max={PAYG_MAX_GENERATIONS}
          value={credits}
          onChange={(event) => setCredits(Number(event.target.value))}
          className="mt-4 h-2 w-full cursor-pointer accent-slate-950"
        />
      </div>

      <ul className="mt-6 flex-1 space-y-3 text-left">
        {PAYG_CARD_COPY.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-[15px] font-medium leading-6 text-slate-800">
            <span
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700"
              aria-hidden
            >
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        href={PAYG_CARD_COPY.cta.href}
        className="mt-7 inline-flex h-14 w-full items-center justify-center rounded-full border border-slate-300 bg-white px-5 text-base font-black text-slate-950 shadow-sm transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-slate-700"
      >
        {PAYG_CARD_COPY.cta.label}
      </Link>
    </article>
  );
}
